import {
  Badge,
  SelectionList,
  SelectionListChangeEvent,
  Text,
} from '@dixa/lib-shared-habla';
import classNames from 'classnames';
import Fuse from 'fuse.js';
import type {
  CustomAttributeFilterControl,
  CustomAttributeFilterControlValue,
  InstrumentPanel,
} from 'lib-analytics-types';
import { ampli } from 'lib-shared-tracking';
import intersection from 'lodash/intersection';
import isEqual from 'lodash/isEqual';
import {
  ElementRef,
  memo,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDebounce } from 'react-use';

import {
  CustomFieldSelectAttribute,
  CustomFieldSelectAttributeOption,
} from 'apis/customFields';
import { useAnalyticsState } from 'app/views/layout/views/analytics/components/panels/utils/useAnalyticsState/useAnalyticsState';
import { convertTreeToFlatItems } from 'utils/tree';

import { isOptionalOperator } from '../../../../../../utils/filters/isOptionalOperator';
import {
  AnalyticsFilterChangeCallback,
  AnalyticsFilterItem,
  AnalyticsFilterLayout,
  AnalyticsFilterOptions,
  AnalyticsFilterSearch,
} from '../../../../../filter';
import styles from '../../analyticsCustomAttributesFilter.module.scss';
import { customAttributesFilterAssets } from '../../utils/customAttributesFilterAssets';

import { CustomFieldSelectAttributeFlatOption } from './analyticsCustomAttributesFilterSelect.types';

type AnalyticsCustomAttributesFilterSelectProps = {
  /** The parent instrument panel, if relevant. */
  panel?: InstrumentPanel;
  /** The filter field. */
  field: CustomAttributeFilterControl['field'];
  /** The custom field select attribute. */
  attribute: CustomFieldSelectAttribute;
  /** The flattened values that are currently selected. */
  selectedValues?: readonly CustomAttributeFilterControlValue[];
  /** The default selected operator. */
  defaultOperator?: CustomAttributeFilterControl['operator'];
  /** The currently selected operator. */
  selectedOperator?: CustomAttributeFilterControl['operator'];
  /** Whether the dropdown is open. */
  isDropdownOpen: boolean;
  /** Callback when the filter is changed. */
  onChange: AnalyticsFilterChangeCallback<CustomAttributeFilterControl>;
  /** Callback for going back to the list of custom attributes. */
  onBack: VoidFunction;
};

const { operators } = customAttributesFilterAssets['select'];

export const AnalyticsCustomAttributesFilterSelect = memo(
  function AnalyticsCustomAttributesFilterSelect({
    panel,
    field,
    attribute,
    attribute: { options },
    selectedValues = [],
    defaultOperator = operators[0],
    selectedOperator = defaultOperator,
    isDropdownOpen,
    onChange,
    onBack,
  }: AnalyticsCustomAttributesFilterSelectProps): JSX.Element {
    const { dashboard } = useAnalyticsState(panel);
    const trackingType = `${field}/${attribute.type}`;

    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

    useDebounce(() => setDebouncedSearchTerm(searchTerm), 300, [searchTerm]);

    const searchRef = useRef<ElementRef<typeof AnalyticsFilterSearch>>(null);
    const isSelectionDisabled = isOptionalOperator(selectedOperator);

    /**
     * Handles changing the filter values.
     *
     * If there is at least one value selected, the filter is either added or
     * updated in the state. Otherwise if no values remain selected, the filter
     * is removed from the state. When selecting a node value, any existing
     * selected child values are automatically removed.
     */
    const handleChange = useCallback(
      ({
        selectedValues,
        selectedItem,
      }: SelectionListChangeEvent<CustomAttributeFilterControlValue>) => {
        const newValues =
          selectedItem.isNode && selectedItem.isSelected
            ? selectedValues.filter((value) => {
                // A value is a child value of the selected node value if it is longer than the node
                // value and has the same first items as the node value.
                const isChildValue =
                  value.length > selectedItem.value.length &&
                  intersection(value, selectedItem.value).length > 0;

                return !isChildValue;
              })
            : selectedValues;

        onChange({
          op: newValues.length ? 'upsert' : 'remove',
          filter: {
            field,
            values: newValues.map((value) => ({ value })),
            operator: selectedOperator,
            attribute_id: attribute.id,
          },
        });

        if (selectedItem.isSelected) {
          ampli.analyticsFilterValueAdded({
            dashboard: dashboard.title,
            panel: panel?.title,
            type: trackingType,
            value: selectedItem.value.join('/'),
            isNode: selectedItem.isNode,
          });
        }
      },
      [
        attribute.id,
        dashboard.title,
        field,
        onChange,
        panel?.title,
        selectedOperator,
        trackingType,
      ],
    );

    /**
     * Handles changing the filter operator.
     *
     * If filter is restored to its default state, i.e. with the default
     * operator and no values selected, the filter is removed from the state.
     *
     * Otherwise if the filter operator is of the optional type, any previously
     * selected values are cleared and the filter is updated in the state.
     */
    const handleChangeOperator = useCallback(
      (operator: CustomAttributeFilterControl['operator']) => {
        const isDefaultState =
          operator === defaultOperator && !selectedValues.length;

        onChange({
          op: isDefaultState ? 'remove' : 'upsert',
          filter: {
            field,
            values: isOptionalOperator(operator)
              ? []
              : selectedValues.map((value) => ({ value })),
            operator,
            attribute_id: attribute.id,
          },
        });

        ampli.analyticsFilterOperatorUpdated({
          dashboard: dashboard.title,
          panel: panel?.title,
          type: trackingType,
          operator,
        });
      },
      [
        attribute.id,
        dashboard.title,
        defaultOperator,
        field,
        onChange,
        panel?.title,
        selectedValues,
        trackingType,
      ],
    );

    /** Handles remove the filter from the state. */
    const handleClear = useCallback(() => {
      onChange({
        op: 'remove',
        filter: {
          field,
          values: [],
          operator: selectedOperator,
          attribute_id: attribute.id,
        },
      });

      ampli.analyticsFilterCleared({
        dashboard: dashboard.title,
        panel: panel?.title,
        type: trackingType,
      });
    }, [
      attribute.id,
      dashboard.title,
      field,
      onChange,
      panel?.title,
      selectedOperator,
      trackingType,
    ]);

    /**
     * The flattened list of options, where each value is represented by a path
     * of all its parents' values. Used when searching, to select leaf values.
     */
    const flatOptions = useMemo(
      () =>
        convertTreeToFlatItems(options, {
          childrenKey: 'options',
          transformItem: (
            option,
            path,
          ): CustomFieldSelectAttributeFlatOption => ({
            ...option,
            label: path
              .map(({ label }) => label)
              .concat([option.label])
              .join(' / '),
            value: path.map(({ value }) => value).concat([option.value]),
          }),
        }).filter(({ options }) => !options?.length),
      [options],
    );

    /** Handles rendering of flat options when searching. */
    const renderFilteredListItems = useCallback(() => {
      const fuse = new Fuse<CustomFieldSelectAttributeFlatOption>(flatOptions, {
        keys: ['label', 'value'],
        threshold: 0.2,
        distance: 200,
      });

      const filteredOptions = fuse
        .search(debouncedSearchTerm)
        .map(({ item }) => item);

      if (!filteredOptions.length) {
        return <AnalyticsFilterLayout.Empty />;
      }

      return filteredOptions.map((option) => (
        <AnalyticsFilterItem
          key={option.id}
          value={option.value}
          label={option.label}
          highlight={debouncedSearchTerm}
        />
      ));
    }, [debouncedSearchTerm, flatOptions]);

    /**
     * Handles rendering a selection list item when the value is a leaf, and
     * when the value is a node and has nested child options, it renders another
     * list recursively.
     */
    const renderListOrItem = useCallback(
      (
        option: CustomFieldSelectAttributeOption,
        parentValuePath: CustomAttributeFilterControlValue = [],
      ) => {
        const valuePath = [...parentValuePath, option.value];

        if (option.options?.length) {
          const isSelected = selectedValues.some((value) =>
            isEqual(value, valuePath),
          );
          const selectedChildValues = selectedValues.filter(
            (value) =>
              value.length > valuePath.length &&
              isEqual(value.slice(0, valuePath.length), valuePath),
          );

          const parentItem = (
            <AnalyticsFilterItem
              key={option.id}
              value={valuePath}
              label={option.label}
              isDisabled={isSelectionDisabled}
              isIndeterminate={selectedChildValues.length > 0}
              contentClassName={classNames(styles.itemContent)}
            >
              {selectedChildValues.length > 0 && (
                <Badge variant="secondary" size="small">
                  {selectedChildValues.length}
                </Badge>
              )}
            </AnalyticsFilterItem>
          );

          // If the parent item is selected, don't render the children.
          if (isSelected) return parentItem;

          return (
            <SelectionList.List key={option.id} parentItem={parentItem}>
              {[...option.options].map((childOption) =>
                renderListOrItem(childOption, valuePath),
              )}
            </SelectionList.List>
          );
        }

        return (
          <AnalyticsFilterItem
            key={option.id}
            value={valuePath}
            label={option.label}
            isDisabled={isSelectionDisabled}
          />
        );
      },
      [isSelectionDisabled, selectedValues],
    );

    /** Handles rendering the selection list items recursively. */
    const renderListItems = useCallback(
      () => options.map((option) => renderListOrItem(option)),
      [options, renderListOrItem],
    );

    return (
      <>
        <AnalyticsFilterLayout.Row borderBottom>
          <AnalyticsFilterSearch
            ref={searchRef}
            value={searchTerm}
            isOpen
            isDisabled={isSelectionDisabled}
            onChange={setSearchTerm}
          />
        </AnalyticsFilterLayout.Row>

        <AnalyticsFilterLayout.Row>
          <Text.Paragraph variant="3" subdued>
            {attribute.label}
          </Text.Paragraph>
        </AnalyticsFilterLayout.Row>

        <AnalyticsFilterLayout.Row noPadding scrollable>
          <SelectionList
            isMultiple
            isActive
            selectedValues={
              selectedValues as CustomAttributeFilterControlValue[]
            }
            onChange={handleChange}
            equalityFn={isEqual}
            defaultActiveItemIndex={1}
          >
            <SelectionList.BackItem onSelected={onBack} />

            {debouncedSearchTerm
              ? renderFilteredListItems()
              : renderListItems()}
          </SelectionList>
        </AnalyticsFilterLayout.Row>

        <AnalyticsFilterLayout.Row borderTop slottable>
          <AnalyticsFilterOptions
            operators={operators}
            defaultOperator={defaultOperator}
            selectedOperator={selectedOperator}
            isMissingSelectedValues={!selectedValues.length}
            isDropdownOpen={isDropdownOpen}
            onChangeOperator={handleChangeOperator}
            onClear={handleClear}
          />
        </AnalyticsFilterLayout.Row>
      </>
    );
  },
);
